/**
 * EL MODELO OFICIAL COMO CONSTANTES (CQ2): qué apartados tiene el cuaderno v9 de
 * la Junta, qué nota lleva cada uno cuando Pest no tiene datos, los códigos
 * cerrados y el vocabulario de los huecos y las preguntas.
 *
 * Todo lo que el motor dice de un valor —de dónde sale y qué falta— se escribe
 * con estos tipos, para que la hoja de revisión (D23), el asistente (CQ4) y los
 * tests hablen igual.
 */

/** Apartados del modelo v9, en su orden. */
export const SECCIONES = [
  "1.1", "1.2", "1.3", "1.4", "2.1", "2.2", "3.1", "3.1bis", "3.2", "3.3", "3.4", "3.5", "4", "5", "6", "7", "7.1", "8", "9", "10",
] as const;
export type Seccion = (typeof SECCIONES)[number];

/**
 * De dónde sale cada valor (hoja de revisión, PLAN §3):
 *   - dato: está en Pest tal cual (el parte, la ficha, el registro);
 *   - calculo: lo calcula el código (dosis/ha, superficie, nº de orden, «TODAS»);
 *   - defecto: valor por defecto (goteo, invernadero, NO en 3.2–3.5) a confirmar;
 *   - usuario: lo contestó una persona (datos legales, confirmar SIGPAC, borrador);
 *   - deducido (CU2, 27-09-2026): el parte NO lo guardó y el código lo deduce con la
 *     MISMA regla que el servidor al completar (aplicador: el asignado de la tarea →
 *     quien lo registró, con su carné vivo; máquina: la de por defecto de la finca →
 *     la de la empresa → la única activa). Se imprime, pero «deducido: confírmalo»:
 *     nunca pasa por guardado hasta que alguien lo confirma en el parte.
 */
export type Origen = "dato" | "calculo" | "defecto" | "usuario" | "deducido";

/** D19: 5, 7 y 8 en blanco con esta nota cuando Pest no tiene nada. */
export const SIN_DATOS = "Sin datos registrados en Ulture Pest";

/** Los tres niveles del guion de preguntas (PLAN §3). */
export type GrupoPregunta = "empresa" | "campana" | "evento";

/**
 * Qué se pregunta. Una clave por pregunta del guion; cada una se salta si el dato
 * ya existe. El texto lo pone quien pregunta (el asistente, en el idioma del
 * usuario): el motor solo dice QUÉ falta, cuánto y a qué afecta.
 */
export type ClavePregunta =
  // Una vez por empresa
  | "razon_social_nif"
  | "rea"
  | "direccion"
  | "titular"
  | "gip_asesor"
  | "carnes"
  | "maquinas"
  | "riego_proteccion"
  | "sigpac"
  | "captaciones_zonas"
  // Por campaña
  | "periodo"
  | "numero_registro"
  | "plaga"
  | "eficacia"
  | "equipo"
  | "aplicador"
  | "confirmar_deducidos"
  | "si_no_3_2_3_5"
  | "analisis"
  // Por evento
  | "equipo_parte"
  | "aplicador_parte"
  | "dosis_parte"
  | "superficie_parte"
  | "cultivo_parte";

export const GRUPO_DE: Record<ClavePregunta, GrupoPregunta> = {
  razon_social_nif: "empresa",
  rea: "empresa",
  direccion: "empresa",
  titular: "empresa",
  gip_asesor: "empresa",
  carnes: "empresa",
  maquinas: "empresa",
  riego_proteccion: "empresa",
  sigpac: "empresa",
  captaciones_zonas: "empresa",
  periodo: "campana",
  numero_registro: "campana",
  plaga: "campana",
  eficacia: "campana",
  equipo: "campana",
  aplicador: "campana",
  confirmar_deducidos: "campana",
  si_no_3_2_3_5: "campana",
  analisis: "campana",
  equipo_parte: "evento",
  aplicador_parte: "evento",
  dosis_parte: "evento",
  superficie_parte: "evento",
  cultivo_parte: "evento",
};

/** Orden del guion: primero lo de la empresa, luego la campaña, luego lo suelto. */
export const ORDEN_GUION: ClavePregunta[] = [
  "razon_social_nif", "rea", "direccion", "titular", "gip_asesor", "carnes", "maquinas", "riego_proteccion", "sigpac", "captaciones_zonas",
  "periodo", "numero_registro", "plaga", "eficacia", "equipo", "aplicador", "confirmar_deducidos", "si_no_3_2_3_5", "analisis",
  "equipo_parte", "aplicador_parte", "dosis_parte", "superficie_parte", "cultivo_parte",
];

/** Por qué un valor está vacío. */
export type MotivoHueco =
  | "falta" // no está en Pest
  | "invalido" // está, pero no pasa el validador (NIF con el control mal, ROPO basura…)
  | "sin_confirmar" // hay un valor derivado que nadie ha confirmado (SIGPAC)
  | "no_convertible" // dosis en unidades o envases: no se pasa a kg o l por ha sin inventar
  | "registro_no_valido" // el nº guardado no es un nº del MAPA (o es el de otro producto)
  | "registro_sin_comprobar" // hay nº, pero sin catálogo del MAPA no se ha podido comprobar (L7, D23)
  | "sin_superficie"; // no se sabe el área tratada

export interface Hueco {
  seccion: Seccion;
  /** Campo del modelo («nif», «numeroRegistro», «dosis»…). */
  campo: string;
  motivo: MotivoHueco;
  /** Índice de la fila en su apartado, si es de una tabla. */
  fila?: number;
  /** Qué filas de Pest lo causan (para ir al parte de origen). */
  partes?: string[];
  lineas?: string[];
  /** Detalle legible para la hoja de revisión (sin datos personales: E11). */
  detalle?: string;
}

export interface Pregunta {
  clave: ClavePregunta;
  grupo: GrupoPregunta;
  /** Cuántas cosas desbloquea contestarla (filas, naves, personas…). */
  cuenta: number;
  /** Producto al que se refiere (nº de registro y plaga van POR PRODUCTO). */
  producto?: string;
  partes?: string[];
  lineas?: string[];
  naves?: string[];
  /** Fincas a las que se refiere (captaciones y zonas del 2.2, por finca; CU7). */
  fincas?: string[];
  /** Lo que el código propone, para contestar con un «sí» (nunca se aplica solo). */
  propuesta?: { valor: string; detalle?: string };
}

/** Clases de producto para el reparto entre apartados. */
export type ClaseLinea = "fitosanitario" | "abono" | "macroorganismo" | "excluida";

/**
 * Enemigos naturales (macroorganismos): no se registran como fitosanitarios.
 * D20: al 3.1 bis como alternativa no química si hay asesor; si no, a
 * Observaciones. Se reconocen por el nombre (género o nombre comercial habitual).
 */
export const MACROORGANISMOS = [
  "nesidiocoris", "macrolophus", "orius", "amblyseius", "swirskii", "swirski", "cucumeris", "neoseiulus", "phytoseiulus",
  "aphidius", "aphidoletes", "aphelinus", "encarsia", "eretmocerus", "chrysoperla", "diglyphus", "dacnusa", "trichogramma",
  "feltiella", "hypoaspis", "stratiolaelaps", "macrocheles", "adalia", "cryptolaemus", "atheta", "dalotia", "transeius",
  "montdorensis", "praon", "ephedrus", "anagyrus", "leptomastix", "steinernema", "heterorhabditis",
] as const;
